import * as Sentry from '@sentry/react'

import { AppThunk } from 'app/store'

import {
  getCarrierProposal,
  submitCarrierInvite,
  createLaneProposal,
  updateLaneProposal,
  destroyLaneProposal,
} from 'services'
import * as carrierProposal from 'services/carrierProposal'
import { parseError } from 'utils/errors'
import { showMessage } from 'features/message/actions'
import { slice, LaneErrors } from './reducer'

const parseLaneErrors = (error: any) => ({
  capacity: error.response?.data.capacity?.join(','),
  rate_price: error.response?.data.rate_price?.join(','),
})

export const {
  clearLaneSuccess,
  fetchRFPStart,
  fetchRFPSuccess,
  fetchRFPFailed,
  submitProposalStart,
  submitProposalSuccess,
  submitProposalFailed,
  submitLaneStart,
  submitLaneSuccess,
  submitLaneFailed,
  laneDeleteSuccess,
  uploadProposalStart,
  uploadProposalSuccess,
  uploadProposalFailed,
  clearUploadErrors,
} = slice.actions

export const fetchRFP = (
  linkUUID: string,
  accepted = false
): AppThunk => async (dispatch) => {
  Sentry.setContext('carrierRFPLinkUUID', { linkUUID })
  dispatch(fetchRFPStart(linkUUID))
  try {
    const data = await getCarrierProposal(linkUUID, accepted)
    dispatch(fetchRFPSuccess(data))
  } catch (err) {
    dispatch(fetchRFPFailed(parseError(err)))
  }
}

export const submitRFP = (
  linkUUID: string,
  rateType?: string
): AppThunk => async (dispatch) => {
  dispatch(submitProposalStart())
  try {
    await submitCarrierInvite(linkUUID, rateType)
    dispatch(submitProposalSuccess())
  } catch (error) {
    if (error.response?.data.errors) {
      dispatch(submitProposalFailed(error.response?.data.errors as LaneErrors))
    } else {
      dispatch(submitProposalFailed(parseError(error)))
    }
  }
}

export const submitLane = (lane: LaneProposal): AppThunk => async (
  dispatch,
  getState
) => {
  const linkUUID = getState().carrierProposal?.uuid as string
  dispatch(submitLaneStart(lane))
  try {
    const data = await createLaneProposal(linkUUID, lane)
    dispatch(submitLaneSuccess(data))
  } catch (error) {
    dispatch(
      submitLaneFailed({
        lane: lane.lane,
        error: parseLaneErrors(error),
      })
    )
  }
}

export const updateLane = (lane: LaneProposal): AppThunk => async (
  dispatch,
  getState
) => {
  const linkUUID = getState().carrierProposal?.uuid as string
  dispatch(submitLaneStart(lane))
  try {
    await updateLaneProposal(linkUUID, lane)
    dispatch(submitLaneSuccess(lane))
  } catch (error) {
    dispatch(
      submitLaneFailed({
        lane: lane.lane,
        error: parseLaneErrors(error),
      })
    )
  }
}

export const deleteLane = (lane: LaneProposal): AppThunk => async (
  dispatch,
  getState
) => {
  if (!lane.id) return
  const linkUUID = getState().carrierProposal?.uuid as string
  dispatch(submitLaneStart(lane))
  try {
    await destroyLaneProposal(linkUUID, lane)
    dispatch(laneDeleteSuccess(lane))
  } catch (error) {
    dispatch(
      submitLaneFailed({
        lane: lane.lane,
        error: parseLaneErrors(error),
      })
    )
  }
}

export const clearLane = (lane: LaneProposal): AppThunk => (dispatch) => {
  dispatch(clearLaneSuccess(lane))
}

export const uploadRFP = (
  linkUUID: string,
  file: File,
  callback?: () => void
): AppThunk => async (dispatch) => {
  dispatch(uploadProposalStart())
  try {
    await carrierProposal.upload(linkUUID, file)
    dispatch(uploadProposalSuccess())
    dispatch(fetchRFP(linkUUID))
    dispatch(
      showMessage(
        {
          message: 'Successfully uploaded lanes.',
          type: 'success',
        },
        8000
      )
    )
    if (callback) callback()
  } catch (err) {
    // TODO: we should return the parseError(err) here, but need to wait
    // for the backend to implement an user friendly error message.
    dispatch(
      uploadProposalFailed(
        'Sorry, there may be some information missing about a lane in your file.'
      )
    )
  }
}
